package org.example.project.enginechat

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.dashboard.DashboardTheme
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.NodeScope
import org.example.project.service.GlobalManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.LoginService
import org.example.project.service.RollenService
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexGrow
import org.jetbrains.compose.web.css.flexShrink
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

private const val CR_CHAT_MESSAGES_CONTAINER_ID = "cr-chat-messages-container"

private val crChatContactsJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun crChatApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private suspend fun loadCrChatUsers(firmenId: String): List<All> {
    if (firmenId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/users/${crChatApiPart(firmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { crChatContactsJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadCrChatGlobalCompanies(currentFirmenId: String): List<All> {
    val raw = runCatching {
        LumaticaApiManager.call("kontakte/global/${crChatApiPart(currentFirmenId)}")
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { crChatContactsJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadCrChatContactData(currentUserId: String, contactCompanyFilterId: String = ""): ChatContactsData {
    val currentUser = LoginService.getUser()
    val currentFirmenId = currentUser?.firmenId.orEmpty().trim()
    val currentChatUserId = globalChatUserId(currentFirmenId, currentUser?.id.orEmpty())
    val effectiveFilterCompanyId = contactCompanyFilterId.trim()
    val internalCompanies = if (currentFirmenId.isBlank()) {
        emptyList()
    } else {
        val internalUsers = loadCrChatUsers(currentFirmenId)
            .asSequence()
            .filter { it.id.isNotBlank() }
            .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
            .filter { it.firmenId == currentFirmenId }
            .filter { canSeeKontaktProfil(currentFirmenId, it) }
            .map { it.copy(id = globalChatUserId(it.firmenId, it.id)) }
            .sortedBy { userDisplayName(it).lowercase() }
            .toList()
        val companyLabel = internalUsers.firstOrNull()?.firma
            .orEmpty()
            .ifBlank { internalUsers.firstOrNull()?.kunde.orEmpty() }
            .ifBlank { currentUser?.kunde.orEmpty() }
        listOf(
            All(
                firmenId = currentFirmenId,
                firma = companyLabel,
                kunde = companyLabel,
                columnSettings = internalUsers
            )
        )
    }

    val externalCompanies = loadCrChatGlobalCompanies(currentFirmenId).mapNotNull { company ->
        val companyId = company.firmenId.trim()
        if (companyId.isBlank() || companyId == currentFirmenId) return@mapNotNull null
        val users = company.columnSettings
            .filterNot { isCurrentChatUser(it, currentFirmenId, currentUserId, currentChatUserId) }
            .sortedBy { userDisplayName(it).lowercase() }
        if (users.isEmpty()) null else company.copy(columnSettings = users)
    }

    val companies = (internalCompanies + externalCompanies).filter {
        it.columnSettings.isNotEmpty() &&
            (effectiveFilterCompanyId.isBlank() || it.firmenId.trim() == effectiveFilterCompanyId)
    }
    val users = companies.flatMap { it.columnSettings }
    return ChatContactsData(
        contactCompanies = companies,
        allUsers = users.associateBy { it.id } + listOfNotNull(currentUser).associateBy {
            globalChatUserId(it.firmenId, it.id)
        }
    )
}

private fun mergeCrContactData(parts: List<ChatContactsData>): ChatContactsData {
    val companiesById = linkedMapOf<String, All>()
    val allUsers = linkedMapOf<String, All>()

    parts.forEach { part ->
        part.allUsers.values.forEach { user ->
            if (user.id.isNotBlank()) allUsers[user.id] = user
        }

        part.contactCompanies.forEach { company ->
            val companyId = company.firmenId.ifBlank { company.id }
            if (companyId.isBlank()) return@forEach
            val existing = companiesById[companyId]
            val mergedUsers = ((existing?.columnSettings).orEmpty() + company.columnSettings)
                .filter { it.id.isNotBlank() }
                .distinctBy { it.id }
                .sortedBy { chatUserShortPrimaryLine(it).lowercase() }
            companiesById[companyId] = company.copy(columnSettings = mergedUsers)
        }
    }

    return ChatContactsData(
        contactCompanies = companiesById.values.toList(),
        allUsers = allUsers
    )
}

@Composable
fun CRChatPanel(
    cr: All,
    embeddedInEngine: Boolean = true,
    onClose: (() -> Unit)? = null
) {
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    val currentUser = LoginService.getUser()
    val currentUserIds = currentUserChatIds()
    val art = if (GlobalManager.currentFirmenId.isBlank()) NodeScope.SYS else NodeScope.CUS
    val currentSenderId = currentSenderChatId(art)
    val canWrite = art == NodeScope.NET || RollenService.hasWritePermission("ChatModuleScreen")
    val currentCompanyId = currentUser?.firmenId.orEmpty().trim()
    val crCompanyId = cr.firmenId.ifBlank { currentCompanyId }.trim()
    val visibleCompanyIds = listOf(crCompanyId, currentCompanyId).filter { it.isNotBlank() }.distinct()
    val roomVersion = GlobalManager.jsonTopicVersion("room.json")

    var room by remember(cr.id) { mutableStateOf<All?>(null) }
    var roomBlocks by remember(cr.id) { mutableStateOf<List<All>>(emptyList()) }
    var contactCompanies by remember(cr.id) { mutableStateOf<List<All>>(emptyList()) }
    var allUsers by remember(cr.id) { mutableStateOf<Map<String, All>>(emptyMap()) }
    var expandedCompanies by remember(cr.id) { mutableStateOf<Set<String>>(emptySet()) }
    var inputText by remember(cr.id) { mutableStateOf("") }
    var isSaving by remember(cr.id) { mutableStateOf(false) }
    var smartLayout by remember(cr.id) { mutableStateOf(true) }

    fun participantIds(activeRoom: All?): Set<String> {
        val baseIds = activeRoom?.let(::roomParticipantIds).orEmpty()
        return (baseIds.toSet() + currentSenderId)
            .filter { it.isNotBlank() }
            .toSet()
    }

    fun syncParticipants(nextParticipants: Set<String>) {
        if (!canWrite) return
        scope.launch {
            ensureCrChatRoom(cr, nextParticipants + currentSenderId).getOrNull()?.let { updated ->
                room = updated
                roomBlocks = loadStandaloneMessages(art, updated.id)
            }
        }
    }

    LaunchedEffect(cr.id, currentCompanyId, crCompanyId) {
        val contactParts = visibleCompanyIds.map { companyId ->
            loadCrChatContactData(
                currentUserId = LoginService.getId().trim(),
                contactCompanyFilterId = companyId
            )
        }
        val merged = mergeCrContactData(contactParts)
        val currentUserEntry = currentUser?.let {
            it.copy(
                id = globalChatUserId(it.firmenId, it.id),
                firmenId = it.firmenId.ifBlank { currentCompanyId },
                firma = it.firma.ifBlank { it.kunde },
                kunde = it.kunde.ifBlank { it.firma }
            )
        }

        val companiesById = merged.contactCompanies.associateBy { it.firmenId.ifBlank { it.id } }.toMutableMap()
        currentUserEntry?.let { self ->
            val selfCompanyId = self.firmenId.ifBlank { currentCompanyId }
            if (selfCompanyId.isNotBlank()) {
                val existing = companiesById[selfCompanyId]
                val label = self.firma.ifBlank { self.kunde.ifBlank { existing?.firma.orEmpty() } }
                val mergedUsers = ((existing?.columnSettings).orEmpty() + self)
                    .filter { it.id.isNotBlank() }
                    .distinctBy { it.id }
                    .sortedBy { chatUserShortPrimaryLine(it).lowercase() }
                companiesById[selfCompanyId] = (existing ?: All(
                    id = selfCompanyId,
                    firmenId = selfCompanyId,
                    firma = label,
                    kunde = label
                )).copy(
                    firma = label,
                    kunde = label,
                    columnSettings = mergedUsers
                )
            }
        }

        val mergedUsers = merged.allUsers.toMutableMap()
        currentUserEntry?.let { mergedUsers[it.id] = it }

        contactCompanies = companiesById.values.toList()
        expandedCompanies = companiesById.keys
        allUsers = mergedUsers

        val initialRoom = loadCrChatRoom(cr.id)
            ?: ensureCrChatRoom(cr, setOf(currentSenderId)).getOrNull()
        room = initialRoom
        roomBlocks = initialRoom?.id?.let { loadStandaloneMessages(art, it) }.orEmpty()
    }

    LaunchedEffect(cr.id, roomVersion) {
        val roomId = crChatRoomId(cr.id)
        if (roomId.isBlank()) return@LaunchedEffect
        val freshRoom = runCatching { ChatApi.loadRoom(roomId) }.getOrNull() ?: room
        room = freshRoom
        roomBlocks = freshRoom?.id?.let { loadStandaloneMessages(art, it) }.orEmpty()
    }

    LaunchedEffect(roomBlocks.size, room?.id) {
        window.setTimeout({
            val container = document.getElementById(CR_CHAT_MESSAGES_CONTAINER_ID) as? HTMLElement
            if (container != null) {
                container.scrollTop = container.scrollHeight.toDouble()
            }
        }, 0)
    }

    fun toggleUser(userId: String, checked: Boolean) {
        val current = participantIds(room)
        val next = if (checked) current + userId else (current - userId) + currentSenderId
        syncParticipants(next)
    }

    fun sendCurrentMessage() {
        if (!canWrite) return
        val activeRoom = room ?: return
        val text = inputText.trim()
        if (text.isBlank() || isSaving) return

        inputText = ""
        isSaving = true

        scope.launch {
            try {
                val result = persistMessage(art, activeRoom, text)
                result.getOrNull()?.let { updated ->
                    room = updated
                    roomBlocks = loadStandaloneMessages(art, updated.id)
                }
            } finally {
                isSaving = false
            }
        }
    }

    val selectedParticipantIds = participantIds(room)
    val selectedOthers = selectedParticipantIds.filter { it !in currentUserIds }

    Div({
        style {
            width(100.percent)
            height(100.percent)
            backgroundColor(DashboardTheme.SURFACE_DEEP)
            property("background", if (embeddedInEngine) DashboardTheme.POPUP_BG else DashboardTheme.PREMIUM_SURFACE_BG)
            padding(16.px)
            boxSizing("border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            overflow("hidden")
            if (!embeddedInEngine) property("border", "1px solid rgba(255,255,255,0.14)")
        }
    }) {
        if (internalTitleVisible) {
            Div({
                style {
                    property("position", "relative")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.SpaceBetween)
                    gap(12.px)
                    color(Color.white)
                    padding(2.px, 28.px, 2.px, 0.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(6.px)
                    }
                }) {
                    Img(src = "images/user.svg", attrs = {
                        style {
                            width(18.px)
                            height(18.px)
                            property("filter", "brightness(0) invert(1)")
                            flexShrink(0)
                        }
                    })
                    Span({
                        style {
                            fontSize(12.px)
                            fontWeight("600")
                            color(Color("rgba(255,255,255,0.72)"))
                        }
                    }) { Text(selectedOthers.size.toString()) }
                }

                Span({
                    style {
                        property("position", "absolute")
                        property("left", "0")
                        property("right", "0")
                        textAlign("center")
                        fontSize(14.px)
                        fontWeight("600")
                        color(Color.white)
                        property("pointer-events", "none")
                    }
                }) { Text(cr.crNummer.ifBlank { "CR Chat" }) }

                if (onClose != null) {
                    Img(src = "images/miniclose.svg", attrs = {
                        onClick { onClose() }
                        style {
                            width(16.px)
                            height(16.px)
                            property("cursor", "pointer")
                            property("filter", "brightness(0) invert(1)")
                            flexShrink(0)
                        }
                    })
                }
            }
        }

        Div({
            style {
                width(100.percent)
                height(1.px)
                backgroundColor(Color("rgba(255,255,255,0.14)"))
                flexShrink(0)
            }
        })

        Div({
            style {
                flexGrow(1)
                minHeight(0.px)
                display(if (smartLayout) DisplayStyle.Flex else DisplayStyle.Grid)
                if (!smartLayout) property("grid-template-columns", "minmax(220px, 28%) minmax(0, 1fr)")
                if (smartLayout) flexDirection(FlexDirection.Column)
                overflow("hidden")
            }
        }) {
            if (!smartLayout) {
                Div({
                    classes(ChatPopupStyles.hiddenScroll)
                    style {
                        minHeight(0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        overflow("hidden")
                        property("padding-right", "18px")
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.FlexStart)
                            padding(4.px, 0.px, 10.px, 0.px)
                        }
                    }) {
                        Span({
                            style {
                                fontSize(11.px)
                                color(Color("rgba(255,255,255,0.62)"))
                                property("text-transform", "uppercase")
                                property("letter-spacing", "0.5px")
                            }
                        }) { Text("Teilnehmer:") }
                    }

                    Div({
                        classes(ChatPopupStyles.hiddenScroll)
                        style {
                            flexGrow(1)
                            minHeight(0.px)
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            overflowY("auto")
                        }
                    }) {
                        if (contactCompanies.isEmpty()) {
                            Div({
                                style {
                                    color(Color("rgba(255,255,255,0.68)"))
                                    fontSize(12.px)
                                    padding(10.px, 0.px)
                                }
                            }) { Text("Keine Kontakte vorhanden.") }
                        } else {
                            contactCompanies.forEach { company ->
                                val companyId = company.firmenId.ifBlank { company.id }
                                val expanded = expandedCompanies.contains(companyId)
                                CompanyHeaderRow(
                                    label = company.firma.ifBlank { company.kunde.ifBlank { companyId.ifBlank { "Firma" } } },
                                    onClick = {
                                        expandedCompanies =
                                            if (expanded) expandedCompanies - companyId
                                            else expandedCompanies + companyId
                                    }
                                )

                                if (expanded) {
                                    company.columnSettings.forEach { user ->
                                        val userId = userChatId(user)
                                        val checked = userId in selectedParticipantIds
                                        val isCurrentUser = userId in currentUserIds

                                        Div({
                                            style {
                                                padding(10.px, 12.px, 10.px, 14.px)
                                                display(DisplayStyle.Flex)
                                                alignItems(AlignItems.Center)
                                                gap(8.px)
                                                property("opacity", if (isCurrentUser) "0.55" else "1")
                                            }
                                        }) {
                                            LumaticaCheckbox(
                                                checked = checked,
                                                onChange = { isChecked ->
                                                    if (!isCurrentUser) toggleUser(userId, isChecked)
                                                },
                                                enabled = canWrite && !isCurrentUser,
                                                fontSizePx = 14
                                            )

                                            Img(src = "images/user.svg", attrs = {
                                                style {
                                                    width(18.px)
                                                    height(18.px)
                                                    property("filter", "brightness(0) invert(1)")
                                                    flexShrink(0)
                                                }
                                            })

                                            Span({
                                                style {
                                                    fontSize(12.px)
                                                    color(Color.white)
                                                    property("overflow", "hidden")
                                                    property("text-overflow", "ellipsis")
                                                    property("white-space", "nowrap")
                                                }
                                            }) {
                                                Text(
                                                    if (isCurrentUser) "${chatUserShortPrimaryLine(user)} (ich)"
                                                    else chatUserShortPrimaryLine(user)
                                                )
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }

            Div({
                style {
                    minHeight(0.px)
                    flexGrow(1)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    overflow("hidden")
                    if (!smartLayout) {
                        property("padding-left", "20px")
                        property("border-left", "1px solid rgba(255,255,255,0.14)")
                    }
                }
            }) {
                Div({
                    attr("id", CR_CHAT_MESSAGES_CONTAINER_ID)
                    classes(ChatPopupStyles.hiddenScroll)
                    style {
                        flexGrow(1)
                        minHeight(0.px)
                        overflowY("auto")
                        padding(6.px, 0.px, 14.px, 0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(10.px)
                    }
                }) {
                    if (roomBlocks.isEmpty()) {
                        Div({
                            style {
                                flexGrow(1)
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                color(Color("rgba(255,255,255,0.7)"))
                                fontSize(14.px)
                                textAlign("center")
                            }
                        }) { Text("Noch keine Nachrichten im CR-Chat.") }
                    } else {
                        ChatMessageList(roomBlocks, { isOwnMessage(it, currentUserIds) }, { chatAuthorLabel(it, currentUserIds, allUsers) })
                    }
                }

                Div({
                    style {
                        minHeight(58.px)
                        padding(8.px, if (smartLayout) 8.px else 0.px, 0.px, 0.px)
                        property("border-top", "1px solid rgba(255,255,255,0.12)")
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        alignItems(AlignItems.Center)
                    }
                }) {
                    Div({
                        style {
                            width(if (smartLayout) 36.px else 28.px)
                            height(if (smartLayout) 36.px else 28.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            property("cursor", "pointer")
                            property("opacity", "0.88")
                            flexShrink(0)
                        }
                        onClick { smartLayout = !smartLayout }
                    }) {
                        Img(src = if (smartLayout) "images/collapsecontent.svg" else "images/expandcontent.svg", attrs = {
                            style {
                                width(if (smartLayout) 22.px else 18.px)
                                height(if (smartLayout) 22.px else 18.px)
                                property("filter", "brightness(0) invert(1)")
                            }
                        })
                    }

                    Input(InputType.Text, attrs = {
                        value(inputText)
                        style {
                            flexGrow(1)
                            height(if (smartLayout) 36.px else 42.px)
                            padding(0.px, if (smartLayout) 14.px else 12.px)
                            boxSizing("border-box")
                            property("border", "1px solid rgba(255,255,255,0.14)")
                            borderRadius(10.px)
                            backgroundColor(Color.transparent)
                            property("background", "transparent")
                            color(Color.white)
                            fontSize(13.px)
                        }
                        onInput { inputText = it.value }
                        onKeyDown {
                            if ((it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") &&
                                inputText.isNotEmpty() &&
                                !isSaving
                            ) {
                                it.preventDefault()
                                sendCurrentMessage()
                            }
                        }
                    })

                    Div({
                        style {
                            width(if (smartLayout) 40.px else 28.px)
                            height(if (smartLayout) 36.px else 28.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            property("cursor", if (inputText.isNotEmpty() && !isSaving) "pointer" else "default")
                            property("opacity", if (inputText.isNotEmpty() && !isSaving) "0.92" else "0.38")
                            flexShrink(0)
                        }
                        if (inputText.isNotEmpty() && !isSaving) onClick { sendCurrentMessage() }
                    }) {
                        Img(src = "images/send.svg", attrs = {
                            style {
                                width(if (smartLayout) 24.px else 28.px)
                                height(if (smartLayout) 24.px else 28.px)
                                property("filter", "brightness(0) invert(1)")
                            }
                        })
                    }
                }
            }
        }
    }
}
